import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  changeUserPassword,
  getFormattedContactNumber,
  getFormattedOptions,
  Toast,
} from "utility/helpers";
import { AddAccount, fetchAllRoles } from "services/services.accounts";
import {
  fetchAllAgencies,
  fetchAllAgencyProjects,
} from "services/services.projects";
import { congressmanSearch } from "services/services.navbar";
import _ from "lodash";
import AccountForm from "views/accounts/detail/Form";
import {
  ACCOUNT_AGENCY_ID,
  ACCOUNT_CONGRESSMAN,
  congRoles,
} from "utility/constants";
import { genericPost } from "services/services.general";
import { ADD_ACTION, EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import { HeaderText } from "assets/js/Styles";
import styled from "styled-components";
import { Col } from "reactstrap";
import useAuth from "hooks/useAuth";
import {
  ALL_AGENCY_OPTION,
  ALL_PROGRAM_OPTION,
  getSelectedAgencies,
  getSelectedPrograms,
  validateAccountForm,
} from "./utility";

const HeaderActions = styled.div`
  margin-top: 24px;
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
`;

const FormSection = styled.div`
  margin-top: 44px;
  padding-bottom: 4rem;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const Account = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { action } = location.state;
  const { user, setUser } = useAuth();
  const row = location.state?.row || null;

  const [agencyOptions, setAgencyOptions] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [accountTypeOptions, setAgencyTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [agenciesOptions, setAgenciesOptions] = useState([]);
  const [agencyPrograms, setAgencyPrograms] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);

  const [formFields, setFormFields] = useState({
    lastname: "",
    firstname: "",
    email: "",
    mobile: "",
    changeEmail: "",
    password: "",
    passwordConfirmation: "",
    agencyType: "",
    agency: "",
    userId: "",
    agencies: [],
    programs: [],
    sendCreds: action === ADD_ACTION,
    accountType: null,
    congressman: null,
    status: false,
  });

  const sendData = (formData) => {
    AddAccount({
      url: action === ADD_ACTION ? "/auth/signup" : "/auth/update-user",
      method: action !== ADD_ACTION ? "PUT" : "POST",
      formData,
      // eslint-disable-next-line consistent-return
      callback: ({ isSuccess, msg }) => {
        setIsLoading(false);

        if (isSuccess) {
          if (action !== VIEW_ACTION) return navigate("/accounts");

          const newUserDetails = {
            ...user,
            email: formData.username,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: formData.phoneNumber,
            programIds: formData.programIds,
          };

          setUser(newUserDetails);

          Toast({
            type: 1,
            content: msg,
          });
        } else {
          Toast({
            type: 4,
            content: msg,
          });
        }
      },
    });
  };

  const updateAccountStatus = () => {
    genericPost({
      url: "auth/update-status",
      formData: {
        status: formFields.status === true ? "Inactive" : "Active",
        username: formFields.email,
        userId: formFields.userId,
      },
    });
  };

  const handleChangePassword = () => {
    genericPost({
      url: "auth/resend-password",
      formData: {
        newPassword: formFields.password,
        username: formFields.email,
      },
    });
  };

  const handleOnSubmit = (values) => {
    const isValid = validateAccountForm(action, formFields);

    if (typeof isValid === "boolean") {
      const formData = {
        username: values.email,
        firstName: values.firstname,
        lastName: values.lastname,
        phoneNumber: values.mobile.slice(1),
        agency:
          values?.accountType?.value === ACCOUNT_AGENCY_ID
            ? values.agency?.value
            : null,
        accountType: values?.accountType?.value,
        congressmanId: values?.congressman?.value || null,
        agencyIds: getSelectedAgencies(values, agencyOptions),
        programIds: getSelectedPrograms(values, programOptions),
      };

      if (action === ADD_ACTION) {
        formData.password = values.password;
        formData.sendCreds = values.sendCreds;
      }

      if (action !== VIEW_ACTION && values.password.length > 0) {
        if (values.changeEmail && values.changeEmail.trim() !== "") {
          formData.email = values.changeEmail.trim();
          formData.password = values.password;
        } else if (action !== ADD_ACTION) {
          handleChangePassword();
          changeUserPassword(values.email, values.password);
        }
      }

      if (action !== ADD_ACTION) formData.userId = values.userId;

      setIsLoading(true);
      sendData(formData);

      if (
        action === EDIT_ACTION &&
        row &&
        Boolean(row.active) !== !values.status
      ) {
        updateAccountStatus();
      }
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const fetchAgencies = () => {
    fetchAllAgencies({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);

        const options = [ALL_AGENCY_OPTION, ...newData];
        setAgencyOptions(newData);
        setAgenciesOptions(options);

        setFormFields((prev) => ({
          ...prev,
          agencies: [options[0]],
        }));
      },
    });
  };

  const fetchRoles = () => {
    fetchAllRoles({
      callback: (data) => {
        if (data?.length !== 0) {
          const options = data
            .filter((option) =>
              user?.type === ACCOUNT_CONGRESSMAN && action === ADD_ACTION
                ? option.id === 7
                : option
            )
            .map((option) => {
              return {
                label: option?.name,
                value: option?.id,
              };
            });

          setAgencyTypeOptions(options);
        }
      },
    });
  };

  const handleCongressmanSearch = _.debounce((inputValue, callback) => {
    congressmanSearch({
      inputValue,
      callback,
    });
  }, 1000);

  const prefillForm = () => {
    const accountType = accountTypeOptions.find(
      (option) => option.label === (row?.type || user?.type)
    );

    setSelectedAccountType(accountType);

    const agency = agencyOptions.find(
      (option) => option.label === (row?.agency || user?.agency)
    );

    const agencies = agencyOptions.filter(
      (option) =>
        row?.agencyIds?.includes(option.value) ||
        user?.agencyIds?.includes(option.value)
    );

    const selectedAgencies =
      agencies.length === agenciesOptions.length - 1
        ? [ALL_AGENCY_OPTION]
        : agencies;

    const congressman =
      // eslint-disable-next-line no-nested-ternary
      row && congRoles.includes(row?.type)
        ? {
            label: row?.congressmanName,
            value: row.congressmanId,
          }
        : congRoles.includes(user?.type)
        ? {
            label: user?.congressman?.name,
            value: user?.congressman?.id,
          }
        : null;

    setFormFields({
      ...formFields,
      firstname: row?.firstName ?? user?.firstName,
      lastname: row?.lastName ?? user?.lastName,
      mobile: getFormattedContactNumber(row?.phoneNumber ?? user?.phoneNumber),
      email: row?.username ?? user?.email,
      accountType,
      agencies: selectedAgencies,
      agency,
      userId: row?.userId ?? user?.userId,
      status: !(row?.active ?? 1),
      congressman,
    });
  };

  const fetchPrograms = () => {
    fetchAllAgencyProjects({
      url: `/agency/${formFields.agency?.value}`,
      callback: (data) => {
        let projects = data?.Projects;
        let options = projects;

        if (options.length !== 0) {
          options = getFormattedOptions("name", projects);
          projects = getFormattedOptions("name", projects);

          options.unshift(ALL_PROGRAM_OPTION);
        }

        setAgencyPrograms(projects);
        setProgramOptions(options);

        if (row || action === VIEW_ACTION) {
          const programs = row
            ? options.filter((option) =>
                row?.programIds?.includes(option.value)
              )
            : options.filter((option) =>
                user?.programIds?.includes(option.value)
              );

          setFormFields((prev) => ({
            ...prev,
            programs:
              programs.length === options.length - 1
                ? [ALL_PROGRAM_OPTION]
                : programs,
          }));
        } else {
          setFormFields((prev) => ({
            ...prev,
            programs: [options[0]],
          }));
        }
      },
    });
  };

  useEffect(() => {
    if (formFields.agency) {
      fetchPrograms();
    }
  }, [formFields.agency]);

  useEffect(() => {
    //  fetch all agencies
    fetchAgencies();

    // fetch role options
    fetchRoles();
  }, []);

  useEffect(() => {
    if (
      action !== ADD_ACTION &&
      accountTypeOptions.length > 0 &&
      agencyOptions.length > 0
    ) {
      prefillForm();
    }
  }, [action, accountTypeOptions, agencyOptions]);

  return (
    <Wrapper>
      <Col className="container">
        <HeaderActions>
          <HeaderText>
            {action === ADD_ACTION ? "Add Account" : "Account Details"}
          </HeaderText>
        </HeaderActions>
        <FormSection>
          <AccountForm
            action={action}
            selectedAccountType={selectedAccountType}
            handleOnSubmit={handleOnSubmit}
            formFields={formFields}
            setSelectedAccountType={setSelectedAccountType}
            accountTypeOptions={accountTypeOptions}
            handleCongressmanSearch={handleCongressmanSearch}
            setFormFields={setFormFields}
            agencyOptions={agencyOptions}
            isLoading={isLoading}
            agenciesOptions={agenciesOptions}
            programOptions={programOptions}
            agencyPrograms={agencyPrograms}
          />
        </FormSection>
      </Col>
    </Wrapper>
  );
};

export default Account;
